import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { Form } from 'react-final-form';
import { LoadingButton } from '@mui/lab';
import { enqueueSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { resolveShipLaterReview } from 'store/thunks/workQueueThunks';

const ShipLaterResolveTarget = {
  VERIFICATION: 'VERIFICATION',
  REVERIFICATION: 'REVERIFICATION',
  VERIFIED: 'VERIFIED',
  IN_SHIPMENT: 'IN_SHIPMENT',
};

const ResolveShipLaterReviewModal = ({ open, onClose, orderId, lineItemId, patientId }) => {
  const dispatch = useDispatch();
  const [targetState, setTargetState] = useState(ShipLaterResolveTarget.VERIFICATION);
  const [loading, setLoading] = useState(false);

  const handleChangeRadio = (event) => setTargetState(event.target.value);

  const handleResolve = () => {
    setLoading(true);
    dispatch(resolveShipLaterReview({ orderId, lineItemId, patientId, targetState })).then(() => {
      onClose();
      setLoading(false);
      enqueueSnackbar('Ship later review resolved!', { variant: 'success' });
    });
  };

  return (
    <Dialog open={open}>
      <DialogTitle>Resolve prescription</DialogTitle>
      <Form
        onSubmit={handleResolve}
        render={({ handleSubmit }) => (
          <form noValidate onSubmit={handleSubmit}>
            <DialogContent sx={{ minWidth: '440px' }}>
              <Grid container direction='column' spacing={1}>
                <Grid item>
                  <Typography>How would you like to resolve this order?</Typography>
                </Grid>
                <Grid item>
                  <Typography>
                    This order is currently in <strong>Verification</strong> status
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography>
                    Resolving will move the order to the selected state below.
                  </Typography>
                </Grid>
                <Grid item sx={{ mt: 1 }}>
                  <Typography>Resolve to:</Typography>
                  <FormControl>
                    <RadioGroup
                      aria-labelledby='ship-later-resolve-options'
                      value={targetState}
                      onChange={handleChangeRadio}
                    >
                      <FormControlLabel
                        value={ShipLaterResolveTarget.VERIFICATION}
                        control={<Radio />}
                        label='Verification'
                      />
                      <FormControlLabel
                        value={ShipLaterResolveTarget.REVERIFICATION}
                        control={<Radio />}
                        label='Reverification'
                      />
                      <FormControlLabel
                        value={ShipLaterResolveTarget.VERIFIED}
                        control={<Radio />}
                        label='Verified'
                      />
                    </RadioGroup>
                    <Typography
                      variant='subtitle1'
                      color='text.secondary'
                      sx={{ mt: 0.5, display: 'block' }}
                    >
                      Note: When Verified is selected and if other items on this order are still
                      being processed, this item will remain in processing until all items are
                      ready.
                    </Typography>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button variant='outlined' color='secondary' onClick={onClose}>
                Cancel
              </Button>
              <LoadingButton loading={loading} variant='contained' type='submit'>
                Confirm
              </LoadingButton>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};

export default ResolveShipLaterReviewModal;

ResolveShipLaterReviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  orderId: PropTypes.string.isRequired,
  lineItemId: PropTypes.string.isRequired,
  patientId: PropTypes.string.isRequired,
};
